import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useLegacyIframeResizeMessage from "../../hooks/useLegacyIframeResizeMessage";
import schema from "../../validation/bookeeSchema";
import { buildSchema } from "../../validation/bookeeSchema";
import LargeContainer from "../../Components/Container/LargeContainer";
import SmallContainer from "../../Components/Container/SmallContainer";
import LoadingScreen from "../../Components/LoadingScreen/LoadingScreen";
import Form from "../../Components/Forms/Form";
import CustomButton from "../../Components/Button/CustomButton";
import { searchExistingBooking, validateForm } from "../../bookingapi/booking.operations";
import SubHeading from "../../Components/Text/SubHeading";
import { Checkbox, FormControl, FormErrorMessage, useToast } from "@chakra-ui/react";
import PopUp from "../../Components/PopUp/PopUp";
import useFormConfig from "../../hooks/useFormConfig";

const LandingPage = () => {
  useLegacyIframeResizeMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { formConfig } = useFormConfig();
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [formData, setFormData] = useState({
    customerFirstName: "",
    customerLastName: "",
    phoneNumber: "",
    emailAddress: "",
    reference: "",
    customerConsent: false,
    notes: ""
  });
  const [preRegToken, setPreRegToken] = useState(null);

  // Initialize form with prefilled data if available
  useEffect(() => {
    if (location.state?.prefillData) {
      const prefill = location.state.prefillData;
      setFormData({
        customerFirstName: prefill.firstName || "",
        customerLastName: prefill.lastName || "",
        phoneNumber: prefill.phone || "",
        emailAddress: prefill.email || "",
        reference: prefill.reference || "",
        customerConsent: false,
        notes: prefill.notes || ""
      });
      setPreRegToken(location.state.preRegToken || null);
    }
  }, [location.state]);

  // prevent user from using browser back button
  useEffect(() => {
    if (window.self !== window.top) {
      // in iframe do nothing
      return;
    }

    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  function handleFormChange(event) {
    const { name, value, type, checked } = event.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  }

  // TODO: fully await or promise not both.
  /**
   * validate
   * get booking window
   * redirect to add-booking passing form data
   */
  const onContinuePress = async () => {
    setIsLoading(true);
    const activeSchema = formConfig ? buildSchema(formConfig) : schema;
    activeSchema
      .validate(formData, { abortEarly: false })
      .then(async () => {
        setErrors({});
        try {
          // Optional form validation against external handler
          const validationResult = await validateForm(formData);
          if (!validationResult?.success) {
            const fieldErrors = {};
            (validationResult.errors || []).forEach((err) => {
              if (err.type === "FIELD") {
                fieldErrors[err.key] = err.value;
              } else if (err.type === "FORM") {
                toast({ title: err.value, status: "error", duration: 5000, isClosable: true });
              }
            });
            if (Object.keys(fieldErrors).length > 0) {
              setErrors(fieldErrors);
            }
            if (!validationResult.errors || validationResult.errors.length === 0) {
              toast({
                title: "Something went wrong. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
            setIsLoading(false);
            return;
          }

          const bookingWindow = validationResult.bookingWindow || null;

          try {
            const existingBooking = await searchExistingBooking(
              formData.phoneNumber,
              formData.emailAddress
            );
            
            const navigationState = {
              ...formData,
              ...(preRegToken && { preRegToken }),
              ...(bookingWindow && { bookingWindow })
            };

            const latestToken = Array.isArray(existingBooking)
              ? existingBooking[0]
              : existingBooking;

            return existingBooking.length === 0 ?
              navigate("/add-booking", { state: navigationState }) :
              navigate(`/update-booking?token=${latestToken}`, { state: { bookingWindow } });
          } catch (error) {
            throw error;
          }

        } catch (error) {
          setIsLoading(false);
          setIsPopUpOpen(true);
        }
      })
      .catch((validationErrors) => {
        const errors = validationErrors.inner.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.path]: curr.message,
          }),
          {}
        );
        setErrors(errors);
        setIsLoading(false);
      });
  };

  const consentConfig = formConfig?.consent || {};
  const consentText = consentConfig.text || "";
  const privacyPolicyUrl = consentConfig.privacyPolicyUrl || "";
  const privacyPolicyLinkText = consentConfig.privacyPolicyLinkText || "Privacy Policy";

  const privacyPolicy = (
    <a href={privacyPolicyUrl} target="_blank" rel="noreferrer">
      {privacyPolicyLinkText}
    </a>
  );

  return (
    <LargeContainer minH={"100vh"}>
      <SmallContainer px={{ base: "2%" }} alignItems={"center"} maxW="600px" margin="0 auto">
        <SmallContainer  alignItems={"center"}>
          {isLoading && (
            <LoadingScreen
              loadingText={"Hold tight"}
              loadingSubText={"It will just take a few moments"}
            />
          )}
          {isPopUpOpen && (
            <PopUp
              popUpHeading={"There's been an issue"}
              popUpMessage={
                "Please review your details, if you have already registered, please check your details match your original booking to retrieve it."
              }
              isOpen={isPopUpOpen}
              onClose={() => setIsPopUpOpen(false)}
            />
          )}
          {!isLoading && (
            // THE ACTUAL FORM
            <SmallContainer backgroundColor={"#FFFFFF"} alignItems={"center"}>
              <Form
                formData={formData}
                handleFormChange={handleFormChange}
                errors={errors}
                formConfig={formConfig}
              ></Form>

              <SmallContainer width={"92.92%"} marginTop="20px">
                <SubHeading
                  fontFamily="Proxima Nova"
                  fontSize="14px"
                  textTransform="none"
                  color="#000000"
                  letterSpacing="0px"
                  fontWeight="600"
                  subHeadingLabel={
                    <>
                      {consentText} {privacyPolicy}
                    </>
                  }
                ></SubHeading>

                <FormControl isInvalid={!!errors.customerConsent}>
                  <Checkbox
                    name="customerConsent"
                    color="#636363"
                    fontFamily="Proxima Nova"
                    marginTop="5px"
                    size="md"
                    isChecked={formData.customerConsent}
                    onChange={handleFormChange}
                  >
                    I Consent
                  </Checkbox>
                  <FormErrorMessage fontSize="12" color="red">
                    {errors.customerConsent}
                  </FormErrorMessage>
                </FormControl>
              </SmallContainer>

              <CustomButton
                marginTop={"32px"}
                marginBottom={"32px"}
                onClick={onContinuePress}
                buttonLabel={"Continue"}
              ></CustomButton>
            </SmallContainer>
          )}
        </SmallContainer>
      </SmallContainer>
    </LargeContainer>
  );
};

export default LandingPage;
